export const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const isApplePlatform = (): boolean =>
  /Mac|iPhone|iPad|iPod/.test(navigator.platform);

export const isSafari = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();

  return (
    userAgent.includes('safari') &&
    !userAgent.includes('chrome') &&
    !userAgent.includes('chromium')
  );
};

/**
 * Wait until the document is visible again (e.g. after snipping UI hides tab).
 */
export const waitForVisibility = async (): Promise<void> =>
  new Promise((resolve) => {
    if (document.visibilityState === 'visible') {
      resolve();

      return;
    }

    const handler = () => {
      if (document.visibilityState === 'visible') {
        document.removeEventListener('visibilitychange', handler);
        resolve();
      }
    };

    document.addEventListener('visibilitychange', handler);
  });

/**
 * Try reading an image blob from clipboard (requires permission / user gesture).
 */
export const tryReadClipboardImage = async (): Promise<Blob | null> => {
  if (!navigator.clipboard || !navigator.clipboard.read) return null;

  try {
    const items = await navigator.clipboard.read();

    for (const item of items) {
      const type = item.types.find((t) => t.startsWith('image/'));

      if (type) return item.getType(type);
    }
  } catch (error: unknown) {
    const errName =
      typeof error === 'object' && error !== null && 'name' in error
        ? String((error as { name?: unknown }).name)
        : '';
    const errMessage =
      typeof error === 'object' && error !== null && 'message' in error
        ? String((error as { message?: unknown }).message)
        : '';

    if (!isSafari() || errName !== 'NotAllowedError') {
      console.warn('Failed to read clipboard image:', errMessage);
    }
  }

  return null;
};

/**
 * Safari-friendly version that reads clipboard during user interaction.
 */
export const tryReadClipboardImageSafari = async (
  _triggerElement: HTMLElement
): Promise<Blob | null> => {
  if (!navigator.clipboard || !navigator.clipboard.read) return null;

  try {
    const items = await navigator.clipboard.read();

    for (const item of items) {
      const type = item.types.find((t) => t.startsWith('image/'));

      if (type) {
        return await item.getType(type);
      }
    }

    return null;
  } catch (error: unknown) {
    const errMessage =
      typeof error === 'object' && error !== null && 'message' in error
        ? String((error as { message?: unknown }).message)
        : '';

    console.warn('Safari clipboard read failed:', errMessage);

    return null;
  }
};

/**
 * Compute a stable SHA-256 hash for a Blob to detect content changes.
 */
export const hashBlob = async (blob: Blob): Promise<string> => {
  try {
    const buffer = await blob.arrayBuffer();
    const digest = await crypto.subtle.digest('SHA-256', buffer);

    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return `${blob?.type || ''}-${blob?.size || ''}`;
  }
};

export interface PollUntilOptions {
  intervalMs?: number;
  isAborted?: () => boolean;
}

/**
 * Polls by calling the provided asynchronous check function until it yields a
 * truthy result or the abort predicate returns true.
 */
export const pollUntil = async <T>(
  checkFn: () => Promise<T | null | undefined>,
  options: PollUntilOptions = {}
): Promise<T | null> => {
  const intervalMs = options.intervalMs ?? 700;
  const isAborted = options.isAborted ?? (() => false);

  for (;;) {
    if (isAborted()) return null;

    const result = await checkFn();

    if (isAborted()) return null;
    if (result) return result;

    await delay(intervalMs);
  }
};
